import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { figureCatalog } from '@/components/organograma/organogramaFigureCatalog';
import type { FluxogramaNodeFigura } from '@/types/fluxograma';
import { BibliotecaFiguras } from './BibliotecaFiguras';

const normalizar = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Seletor da forma "Figura" do fluxograma: ícones do catálogo + imagens da biblioteca da empresa. */
export function FiguraPicker({
  empresaId,
  value,
  onChange,
  disabled = false,
}: {
  empresaId?: string | null;
  value?: FluxogramaNodeFigura;
  onChange: (figura: FluxogramaNodeFigura) => void;
  disabled?: boolean;
}) {
  const [busca, setBusca] = useState('');
  const termo = normalizar(busca.trim());
  const catalogo = termo ? figureCatalog.filter((item) => normalizar(item.label).includes(termo)) : figureCatalog;

  return (
    <div className="space-y-3">
      <div>
        <Label>Figura</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          {figureCatalog.length} ícones prontos ou uma imagem das figuras da empresa.
        </p>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
          placeholder="Buscar ícone por nome..."
          className="h-8 pl-8 text-xs"
          disabled={disabled}
        />
      </div>

      <div className="grid max-h-64 grid-cols-3 gap-2 overflow-y-auto rounded-2xl border border-border bg-muted/30 p-2">
        {catalogo.length === 0 && (
          <p className="col-span-3 py-4 text-center text-xs text-muted-foreground">Nenhum ícone encontrado.</p>
        )}
        {catalogo.map((item) => {
          const Icon = item.icon;
          const selecionado = value?.tipo === 'catalogo' && value.chave === item.key;
          return (
            <button
              key={item.key}
              type="button"
              disabled={disabled}
              title={item.label}
              onClick={() => onChange({ tipo: 'catalogo', chave: item.key })}
              className={cn(
                'flex flex-col items-center gap-1 overflow-hidden rounded-xl border border-border bg-background px-1.5 py-2 text-[11px] text-muted-foreground shadow-sm transition hover:border-primary/40 hover:text-foreground',
                selecionado && 'border-primary ring-2 ring-primary/20 text-foreground',
              )}
            >
              <Icon className="h-5 w-5 shrink-0 text-foreground" />
              <span className="w-full truncate text-center">{item.label}</span>
            </button>
          );
        })}
      </div>

      <BibliotecaFiguras
        empresaId={empresaId}
        disabled={disabled}
        isSelecionada={(figura) => value?.tipo === 'biblioteca' && value.figura_id === figura.id}
        onSelect={(figura) => onChange({ tipo: 'biblioteca', figura_id: figura.id, url: figura.url })}
      />
    </div>
  );
}
